import React, { useRef } from "react";
import HeroRings from "./hero/HeroRings";
import HeroLogo from "./hero/HeroLogo";
import HeroCTAs from "./hero/HeroCTAs";
import HeroMomentumWave from "./hero/HeroMomentumWave";
import { useCursorParallax } from "../../hooks/useCursorParallax";
import { useIsDesktop } from "../../hooks/useIsDesktop";

const Hero = () => {
  const sceneRef = useRef(null);
  const isDesktop = useIsDesktop(1024);
  // Cursor-driven parallax is meaningless on touch devices and adds a
  // constant RAF loop. Only attach it when we know we're on a fine-pointer
  // viewport.
  useCursorParallax(isDesktop ? sceneRef : null);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={sceneRef}
      className="parallax-root relative min-h-screen flex items-center justify-center overflow-hidden noise-overlay"
      style={{ "--mx": 0, "--my": 0 }}
    >
      <div className="stardust" aria-hidden />
      <div className="absolute inset-0 gold-glow-radial pointer-events-none" />
      {/* HeroMomentumWave carries 10 SVG strands with Gaussian blur filters
          + 26 animated dust dots — even when display:none, mobile WebKit
          builds the filter graph. Mount it ONLY on real desktop viewports
          so phones get a clean, cheap first paint. */}
      {isDesktop && <HeroMomentumWave />}
      <div className="hero-vignette absolute inset-0 pointer-events-none" aria-hidden />

      <HeroRings />

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center pt-28 pb-20">
        <HeroLogo />

        <p
          data-testid="hero-eyebrow"
          className="text-[11px] md:text-xs tracking-[0.5em] uppercase text-gold mb-8 animate-fade-in-up"
          style={{ animationDelay: "150ms" }}
        >
          A Book · A Method · A Movement
        </p>

        <h1
          data-testid="hero-headline"
          className="parallax-layer font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[80px] leading-[1.04] tracking-tight text-themed animate-fade-in-up"
          style={{ animationDelay: "250ms", fontWeight: 300, "--depth": "4px" }}
        >
          Turn scattered potential into{" "}
          <span className="italic gold-gradient-text">structured momentum.</span>
        </h1>

        <p
          data-testid="hero-subheadline"
          className="mt-8 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-themed-muted animate-fade-in-up"
          style={{ animationDelay: "400ms" }}
        >
          For founders, creators, visionaries, and transition-stage professionals who know they are meant for more, but need clarity, structure, and a practical next move.
        </p>

        <p
          data-testid="hero-snapshot-trust"
          className="mt-5 text-[11px] tracking-[0.3em] uppercase text-gold/85 animate-fade-in-up"
          style={{ animationDelay: "500ms" }}
        >
          Authentic Momentum helps you understand what you are carrying, where movement is blocked, and what structure turns insight into action.
        </p>

        <HeroCTAs />
      </div>
    </section>
  );
};

export default Hero;
